import { ChartAxesOptions } from "../../components/charts/use/chart-axes-options";
import { createRestrictiveInjectionState } from "../../composable/restrictive-injection-state";
import { useSnackbarStore } from "../snackbar";
import { useDashboardCommentsProvider } from "./dashboard-comment";
import { provideDashboardEditor } from "./dashboard-editor";
import { provideDashboardLayout } from "./dashboard-layout";
import { provideDashboardLiveData } from "./dashboard-live-data";
import { provideDashboardState } from "./dashboard-state";
import { provideTimeRangeManager } from "./dashboard-time";

export const [useDashboardProvider, useDashboard] = createRestrictiveInjectionState(() => {
	const state = provideDashboardState();

	const timeManager = provideTimeRangeManager(state.timezone, state.locale);
	const layouts = provideDashboardLayout();

	useDashboardCommentsProvider();
	provideDashboardLiveData();
	const editor = provideDashboardEditor();

	const allowPanelClick = computed(() => !state.lockCursor.value);

	watch(
		timeManager.rangeMode,
		(mode) => {
			if (mode === "custom" && state.liveMode.value) {
				state.liveMode.value = false;
			}
		},
		{ immediate: true }
	);

	watch(
		state.liveMode,
		(live) => {
			if (!live) {
				state.lockCursor.value = false;
			}

			if (live && timeManager.rangeMode.value === "custom") {
				timeManager.updateRangeMode("last_8h");
			}
		},
		{ immediate: true }
	);

	watch(state.lockCursor, (isLocked, wasLocked) => {
		if (!wasLocked && isLocked) {
			timeManager.updateCursor(timeManager.range.value.end.minus({ second: 5 }));
		}
	});

	const snackbar = useSnackbarStore();
	const { t } = useI18n();

	function updateCursor(date: Date, force = false) {
		if (state.lockCursor.value && !force) {
			snackbar.openOnError(t("dashboard.cursorLockError").toString());
			return;
		}

		timeManager.updateCursor(date);
	}

	const hasRightData = computed(() => {
		return (
			layouts.currentLayout.value?.panels.some((panel) => {
				if (panel.plugin.type !== "timehistory" && panel.plugin.type !== "spectrum") return false;

				const options = panel.plugin.options;
				if (!options) return false;

				const axes = Object.values(options) as ChartAxesOptions[];
				return axes.some((axis) => axis.right?.visible);
			}) || false
		);
	});
	const pluginLayout = computed(() => {
		const right = hasRightData.value ? 80 : 48;
		const marginRight = hasRightData.value ? 30 : 0;
		return { right, marginRight };
	});

	// Make the api work like a pinia store, for compatibility.
	return reactive({
		...timeManager,
		...state,
		editor,
		pluginLayout,
		layouts,
		allowPanelClick,
		updateCursor,
	});
});
